import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
} from "ag-grid-community";
import { apiGet, apiPut } from "../../services/genesisApi/httpsMethods";
import type { ApiResponse } from "../../services/genesisApi/httpsMethods";
import { CustomButton } from "../../components/common/customButton";
import { ErrorDialog } from "../../components/common/errorDialog";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { areRecordsEqual, getChangedRecords } from "../../utils/compareData";
import { CustomTextInput } from "../../components/common/inputFields/customTextInput";
import { CustomMultiSelect } from "../../components/common/inputFields/customMultiSelect";
import type { Asset, Accordion } from "../../types/deal";
import { HyperLinkRenderer } from "../../utils/hyperlinkRenderer";
import {
  numberFormatter,
  dateFormatter,
  dateTimeFormatter,
  csvExportCell,
} from "../../utils/numberFormatter";
import {
  createAreaTypeSelectColumn,
  createObjectArraySelectColumn,
  createStringSelectColumn,
} from "../../utils/columnHelpers";
import { normalizeAssetData } from "../../utils/dealValidation";
import { useReferenceLists } from "../../context/GlobalGetLists";
import "../../assets/css/Text-General.css";
import "../../assets/css/AGGrid-custom.css";
import AssetMap from "../../components/common/maps/AssetMap";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import { CustomIconButton } from "../../components/common/customIconButton";
import pencilIcon from "@/assets/svg/edit.svg";
import pinIcon from "@/assets/svg/pin-icon.svg";
import { useCurrentUser } from "@/context/CurrentUserContext";
import downloadIcon from "@/assets/svg/downloads-dark.svg";
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

ModuleRegistry.registerModules([AllCommunityModule]);

type AssetsProps = {
  dealId?: number | null;
  accordionValues?: Accordion[];
};

const Assets = ({ dealId = null, accordionValues }: AssetsProps) => {
  const { canEdit } = useCurrentUser();
  const isEmbedded = Boolean(dealId);
  const [editMode, setEditMode] = useState(false);
  const gridRef = useRef<AgGridReact>(null);
  const [filter, setFilter] = useState("");
  const [dirtyGrid, setDirtyGrid] = useState(false);
  const [newAssets, setNewAssets] = useState<Asset[]>([]);
  const [initialAssets, setInitialAssets] = useState<Asset[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();
  const [mapView, setMapView] = useState(false);
  const [selectedSectors, setSelectedSectors] = useState<(string | number)[]>(
    [],
  );
  const [selectedCountries, setSelectedCountries] = useState<
    (string | number)[]
  >([]);
  const [accordions, setAccordions] = useState<Accordion[]>(
    accordionValues ?? [],
  );
  const { sectors, countries, deals, currencies } = useReferenceLists();

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const endpoint = dealId ? `assets?dealId=${dealId}` : "assets";
        const response = await apiGet(endpoint);

        if (response.success && response.data) {
          setNewAssets(response.data);
          setInitialAssets(structuredClone(response.data)); // Create a deep copy for initial state
        } else {
          console.error("Failed to fetch assets:", response.message);
        }
      } catch (error) {
        console.error("Failed to fetch assets", error);
      }
    };

    fetchAssets();
  }, [dealId]);

  useEffect(() => {
    const fetchAccordions = async () => {
      if (!dealId) {
        setAccordions([]);
        return;
      }

      try {
        const response = await apiGet(`deals/${dealId}/accordions`);
        if (response.success && response.data) {
          const fetchedAccordions = Array.isArray(response.data)
            ? response.data
            : [];
          setAccordions(fetchedAccordions);
        }
      } catch (error) {
        console.error("Failed to fetch accordions:", error);
        setAccordions([]);
      }
    };

    fetchAccordions();
    // Refresh accordions when window becomes visible (e.g., after page reload)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && dealId) {
        fetchAccordions();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dealId]);

  useEffect(() => {
    const isEqual = areRecordsEqual(initialAssets, newAssets);
    setDirtyGrid(!isEqual);
  }, [initialAssets, newAssets]);

  const onCellValueChanged = useCallback((event: any) => {
    const { newValue, oldValue, data } = event;
    if (newValue === oldValue) return;
    // Snapshot the whole row so valueSetter side-effects (e.g. primary_sector_id) flow into state.
    setNewAssets((prevAsset: Asset[]) =>
      prevAsset.map((asset) => (asset.id === data.id ? { ...data } : asset)),
    );
  }, []);

  const handleCancelButtonPress = () => {
    setEditMode(false);
    // Clone so AG Grid edits don't mutate the baseline rows.
    setNewAssets(structuredClone(initialAssets)); // Reset to original
    setDirtyGrid(false);
  };

  const handleSaveButtonPress = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      // Save asset changes
      const changedItems = getChangedRecords(newAssets, initialAssets);
      const updates = changedItems
        .filter((asset) => typeof asset.id === "number")
        .map((asset) => {
          const normalizedAsset = normalizeAssetData(asset);
          return apiPut("asset", asset.id, normalizedAsset).catch(
            (error): ApiResponse => {
              console.error(`Failed to update asset ${asset.id}`, error);
              return {
                success: false,
                status: 0,
                message: "Failed to save asset changes. Please try again.",
              };
            },
          );
        });
      const results = await Promise.all(updates);
      const failedResponse = results.find((r) => !r?.success);
      if (failedResponse) {
        // Stay in edit mode and keep the user's edits so they can retry.
        setIsSaving(false);
        showErrorFromResponse(failedResponse);
        return;
      }

      window.location.reload();
    } catch (error) {
      console.error("Error saving assets and accordions:", error);
      setIsSaving(false);
      showErrorFromResponse({
        success: false,
        status: 0,
        message: "Failed to save changes. Please try again.",
      });
    }
  };

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSaveButtonPress);

  const filteredRows = useMemo(() => {
    let filtered = newAssets?.filter((s) =>
      s.asset_name?.toLowerCase().includes(filter.toLowerCase()),
    );

    // Filter by sectors if selected
    if (selectedSectors.length > 0) {
      filtered = filtered?.filter((asset) => {
        const assetSector =
          Array.isArray(asset.sector) && asset.sector.length > 0
            ? asset.sector[0].id
            : (asset.sector as any)?.id;
        return selectedSectors.includes(assetSector);
      });
    }

    // Filter by countries if selected
    if (selectedCountries.length > 0) {
      filtered = filtered?.filter((asset) => {
        const assetCountry =
          Array.isArray(asset.country) && asset.country.length > 0
            ? asset.country[0].id
            : (asset.country as any)?.id;
        return selectedCountries.includes(assetCountry);
      });
    }

    return filtered;
  }, [newAssets, filter, selectedSectors, selectedCountries]);

  const columnDefs: ColDef[] = useMemo(() => {
    const cols: ColDef[] = [
      {
        field: "asset_name",
        headerName: "Asset",
        editable: editMode,
        width: 150,
        cellRenderer: HyperLinkRenderer("Assets"),
      },
    ];

    // Deal column - read-only (no editing in table)
    cols.push({
      field: "deal.deal_name",
      headerName: "Deal",
      editable: false,
      width: 150,
      cellRenderer: (props: any) => {
        const deal = props.data?.deal;
        if (!deal) return null;
        const dealObj =
          typeof deal === "object"
            ? deal
            : (deals || []).find((d: any) => d.id === deal);
        if (!dealObj) return null;
        return HyperLinkRenderer("Deals", dealObj.id, dealObj.deal_name)(props);
      },
    });

    // Accordion column - always show, read-only (editing handled in ExtractedAssetsTable)
    cols.push({
      field: "accordion",
      headerName: "Addon",
      editable: false,
      width: 150,
      valueGetter: (params: any) => {
        const assetId = params.data?.id;
        if (!assetId || !accordions?.length) return null;

        // Find first matching accordion (asset can only be in one)
        const matchingAccordion = accordions.find(
          (accordion) =>
            accordion.asset_ids &&
            Array.isArray(accordion.asset_ids) &&
            accordion.asset_ids.includes(assetId),
        );

        return matchingAccordion ? matchingAccordion.accordion_name : null;
      },
      valueFormatter: (params: any) => {
        return params.value || "-";
      },
    });

    // Add all other columns
    cols.push(
      {
        field: "address_1",
        headerName: "Address 1",
        editable: editMode,
        width: 150,
      },
      {
        field: "address_2",
        headerName: "Address 2",
        editable: editMode,
        width: 100,
      },
      { field: "city", headerName: "City", editable: editMode, width: 100 },
      {
        field: "latitude",
        headerName: "Latitude",
        editable: editMode,
        width: 100,
      },
      {
        field: "longitude",
        headerName: "Longitude",
        editable: editMode,
        width: 100,
      },
      createObjectArraySelectColumn(
        "country",
        "Country",
        countries || [],
        "nicename",
        100,
        editMode,
        "country_id",
      ),
      createStringSelectColumn(
        "currency",
        "Currency",
        currencies || [],
        100,
        editMode,
      ),
      {
        field: "valuation_amount",
        headerName: "Valuation",
        editable: editMode,
        width: 100,
        valueFormatter: numberFormatter,
      },
      {
        field: "valuation_date",
        headerName: "Valuation Date",
        editable: editMode,
        width: 100,
        valueFormatter: dateFormatter,
      },
      {
        field: "acquisition_price",
        headerName: "Acquisition Price",
        editable: editMode,
        width: 100,
        valueFormatter: numberFormatter,
      },
      {
        field: "acquisition_date",
        headerName: "Acquisition Date",
        editable: editMode,
        width: 100,
        valueFormatter: dateFormatter,
      },
      {
        headerName: "GIY (acquitision)",
        field: "giy_acquisition",
        editable: false,
        width: 100,
        valueGetter: (params) => {
          const price = params.data?.acquisition_price;
          const rent = params.data?.contracted_rent;
          if (price && rent) {
            return (rent / price) * 100;
          }
          return null;
        },
        valueFormatter: ({ value }) => {
          if (value == null || isNaN(value)) return "";
          return (
            Number(value).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) + " %"
          );
        },
      },
      {
        headerName: "GIY (valuation)",
        field: "giy_valuation",
        editable: false,
        width: 100,
        valueGetter: (params) => {
          const price = params.data?.valuation_amount;
          const rent = params.data?.contracted_rent;
          if (price && rent) {
            return (rent / price) * 100;
          }
          return null;
        },
        valueFormatter: ({ value }) => {
          if (value == null || isNaN(value)) return "";
          return (
            Number(value).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) + " %"
          );
        },
      },
      {
        field: "total_area",
        headerName: "Area",
        editable: editMode,
        width: 100,
        valueFormatter: numberFormatter,
      },
      createAreaTypeSelectColumn(editMode, 100),
      {
        field: "passing_rent",
        headerName: "Passing Rent",
        editable: editMode,
        width: 100,
        valueFormatter: numberFormatter,
      },
      {
        headerName: "Passing Rent / Area",
        field: "passing_rent_per_area",
        editable: false,
        width: 100,
        valueGetter: (params) => {
          const passing_rent = params.data?.passing_rent;
          const area = params.data?.total_area;
          if (passing_rent && area) {
            return passing_rent / area;
          }
          return null;
        },
        valueFormatter: ({ value }) => {
          if (value == null || isNaN(value)) return "";
          return Number(value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      },
      {
        field: "contracted_rent",
        headerName: "Contracted Rent",
        editable: editMode,
        width: 100,
        valueFormatter: numberFormatter,
      },
      {
        headerName: "Contracted Rent / Area",
        field: "contracted_rent_per_area",
        editable: false,
        width: 100,
        valueGetter: (params) => {
          const contracted_rent = params.data?.contracted_rent;
          const area = params.data?.total_area;
          if (contracted_rent && area) {
            return contracted_rent / area;
          }
          return null;
        },
        valueFormatter: ({ value }) => {
          if (value == null || isNaN(value)) return "";
          return Number(value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      },
      {
        field: "estimated_rental_value",
        headerName: "ERV",
        editable: editMode,
        width: 100,
        valueFormatter: numberFormatter,
      },
      {
        headerName: "ERV / Area",
        field: "erv_per_area",
        editable: false,
        width: 100,
        valueGetter: (params) => {
          const erv = params.data?.estimated_rental_value;
          const area = params.data?.total_area;
          if (erv && area) {
            return erv / area;
          }
          return null;
        },
        valueFormatter: ({ value }) => {
          if (value == null || isNaN(value)) return "";
          return Number(value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      },

      { field: "walt", headerName: "WALT", editable: editMode, width: 100 },
      { field: "walb", headerName: "WALB", editable: editMode, width: 100 },
      {
        field: "epc_rating",
        headerName: "EPC",
        editable: editMode,
        width: 100,
      },
      createObjectArraySelectColumn(
        "sector",
        "Sector",
        sectors || [],
        "sector",
        100,
        editMode,
        "primary_sector_id",
      ),
      {
        field: "year_built",
        headerName: "Year Built",
        editable: editMode,
        width: 100,
      },
      {
        field: "year_refurbished",
        headerName: "Year refurbished",
        editable: editMode,
        width: 100,
      },
      {
        field: "count_buildings",
        headerName: "No. of Buildings",
        editable: editMode,
        width: 100,
      },
      {
        field: "count_floors",
        headerName: "No. of Floors",
        editable: editMode,
        width: 100,
      },
      {
        field: "count_rooms",
        headerName: "No. of Rooms",
        editable: editMode,
        width: 100,
      },
      {
        field: "count_boxes",
        headerName: "No. of Boxes",
        editable: editMode,
        width: 100,
      },
      {
        field: "count_units",
        headerName: "No. of Units",
        editable: editMode,
        width: 100,
      },
      {
        field: "opex_annual",
        headerName: "OPEX (Annual)",
        editable: editMode,
        width: 100,
        valueFormatter: numberFormatter,
      },
      {
        field: "noi",
        headerName: "NOI",
        editable: editMode,
        width: 100,
        valueFormatter: numberFormatter,
      },
      {
        field: "capex_total",
        headerName: "Capex Total",
        editable: editMode,
        width: 100,
        valueFormatter: numberFormatter,
      },
      {
        field: "created_at",
        headerName: "Created Date",
        editable: false,
        width: 120,
        valueFormatter: dateTimeFormatter,
      },
      {
        field: "updated_at",
        headerName: "Updated Date",
        editable: false,
        width: 120,
        valueFormatter: dateTimeFormatter,
      },
    );

    return cols;
  }, [editMode, accordions, deals, dealId, sectors, countries, currencies]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        padding: isEmbedded ? "20px 0px 20px 0px" : 0,
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {!isEmbedded && (
        <Box sx={{ flexShrink: 0 }}>
          <SimpleToolbar>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: "15px", minWidth: 0 }}
            >
              <CustomTextInput
                label="Asset name"
                value={filter}
                onChange={(val) => typeof val === "string" && setFilter(val)}
                fullWidth={false}
              />
              <CustomMultiSelect
                value={selectedSectors}
                onChange={(e) =>
                  setSelectedSectors(e.target.value as (string | number)[])
                }
                options={sectors || []}
                labelKey="sector"
                placeholder="All Sectors"
                minWidth="150px"
              />
              <CustomMultiSelect
                value={selectedCountries}
                onChange={(e) =>
                  setSelectedCountries(e.target.value as (string | number)[])
                }
                options={countries || []}
                labelKey="nicename"
                placeholder="All Countries"
                minWidth="150px"
              />
            </Box>

            {editMode ? (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: "15px" }}
                data-position="right"
              >
                <CustomButton
                  text="Cancel"
                  handleButtonPress={handleCancelButtonPress}
                  isButtonDisabled={isSaving}
                  solid={false}
                  widthOverride="100px"
                />
                <CustomButton
                  text="Save"
                  handleButtonPress={requestSave}
                  isButtonDisabled={(!dirtyGrid && !isCellEditing) || isSaving}
                  isLoading={isSaving}
                  isLoadingText="Saving"
                  solid
                  widthOverride="100px"
                />
              </Box>
            ) : (
              <Box
                data-position="right"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.25,
                  ml: -0.5,
                  "& .MuiIconButton-root": {
                    p: 0.6,
                  },
                  "& .MuiIconButton-root .MuiSvgIcon-root": {
                    fontSize: 22,
                  },
                }}
              >
                <CustomIconButton
                  tooltip="Download"
                  icon={
                    <Box
                      component="img"
                      src={downloadIcon}
                      alt="Download assets"
                      sx={{ width: 30, height: 30, display: "block" }}
                    />
                  }
                  iconButtonSx={{ p: 0 }}
                  onClick={() =>
                    gridRef.current?.api.exportDataAsCsv({
                      fileName: "assets",
                      processCellCallback: csvExportCell,
                    })
                  }
                />
                <CustomIconButton
                  tooltip="Map"
                  icon={
                    <Box
                      component="img"
                      src={pinIcon}
                      alt="Toggle map"
                      sx={{ width: 20, height: 20, display: "block" }}
                    />
                  }
                  onClick={() => setMapView(!mapView)}
                />
                <CustomIconButton
                  tooltip="Edit"
                  icon={
                    <Box
                      component="img"
                      src={pencilIcon}
                      alt="Edit assets"
                      sx={{ width: 20, height: 20, display: "block" }}
                    />
                  }
                  disabled={!canEdit}
                  onClick={() => {
                    if (!canEdit) return;
                    setEditMode(true);
                  }}
                />
              </Box>
            )}
          </SimpleToolbar>
        </Box>
      )}

      {/* Map View */}
      {mapView && (
        <Box sx={{ marginBottom: "20px", flexShrink: 0 }}>
          <AssetMap
            assets={filteredRows}
            height={700}
            accordions={undefined}
            showAssetNumbers={false}
            hideFullscreenButton={true}
          />
        </Box>
      )}

      {/* AG Grid */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          height: isEmbedded ? "100%" : mapView ? "400px" : "100%",
          p: 0,
        }}
      >
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact
            {...gridEditProps}
            rowData={filteredRows}
            ref={gridRef}
            columnDefs={columnDefs}
            onCellValueChanged={onCellValueChanged}
            singleClickEdit={true}
            defaultColDef={{ resizable: true }}
            rowHeight={30}
            getRowId={({ data }) => data?.id?.toString()}
          />
        </div>
      </Box>
      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

export default Assets;
